import { RefObject, createRef, PureComponent } from 'react';
import { ScrollPane } from './styled';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_DIVERSIFICATION_BY_COMPANY,
  PORTFOLIO_DIVERSIFICATION_BY_INDUSTRY,
} from '@/constants/feedbackSections';
import {
  PORTFOLIO_DOWNLOAD_DIVERSIFICATION_COMPANY,
  PORTFOLIO_DOWNLOAD_DIVERSIFICATION_INDUSTRY,
} from '@/constants/downloadSections';
import { getPortfolioInfographicDownloadUrl } from '@utilities/urlGenerator';
import type { ComputedPortfolioHolding } from '@/components/DeprecatedPortfolio/redux/interface';
import loadable from '@loadable/component';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';

const NoData = loadable(() => import('@components/NoData'), {
  resolveComponent: (component) => component.NoData,
});
const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

const HoldingsDiversificationChart = loadable(
  () => import('../HoldingsDiversification'),
  {
    ssr: false,
    resolveComponent: (component) => component.HoldingsDiversificationChart,
  }
);

const DendogramChart = loadable(() => import('../DendogramChart'), {
  resolveComponent: (component) => component.DendogramChart,
  ssr: false,
});
export interface DiversificationProps {
  holdings?: ComputedPortfolioHolding[];
  fundValue?: number;
  totalValue: number | null;
  secretCode?: string;
  portfolioId: string;
  isWatchlist: boolean;
  sectionNumber?: number;
  portfolioName: string;
  lastUpdated: number;
}

class Diversification extends PureComponent<DiversificationProps> {
  private scrollPane: RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this.scrollPane = createRef<HTMLDivElement>();
  }

  componentDidMount() {
    const {
      scrollPane: { current },
    } = this;
    if (current && this.scrollPane.current) {
      this.scrollPane.current.scrollLeft =
        current.scrollWidth - current.offsetWidth;
    }
  }

  render() {
    const { scrollPane } = this;
    const {
      portfolioId,
      sectionNumber,
      holdings,
      fundValue,
      totalValue,
      portfolioName,
      secretCode,
      isWatchlist,
      lastUpdated,
    } = this.props;

    const downloadDiversificationByCompanyUrl =
      getPortfolioInfographicDownloadUrl(
        portfolioId,
        PORTFOLIO_DOWNLOAD_DIVERSIFICATION_COMPANY,
        secretCode || ''
      );

    const downloadDiversificationByIndustryUrl =
      getPortfolioInfographicDownloadUrl(
        portfolioId,
        PORTFOLIO_DOWNLOAD_DIVERSIFICATION_INDUSTRY,
        secretCode || ''
      );

    return (
      <ReportSection sectionNumber={sectionNumber}>
        <ReportSectionHeader
          title="Diversification"
          sectionDescription="The Portfolio diversification across companies and industries"
        />
        {!isWatchlist && (
          <>
            <ReportSubSection
              title="Diversification By Company"
              trackingProps={{ subject: 'chart' }}
              body={
                holdings && holdings.length > 0 && totalValue !== null ? (
                  <DeferRenderUntilIntersecting preRenderHeight={400}>
                    {() => (
                      <>
                        <HoldingsDiversificationChart
                          totalValue={totalValue}
                          portfolioName={portfolioName}
                          holdings={holdings}
                          portfolioId={portfolioId}
                          lastUpdated={lastUpdated}
                        />
                        <ChartActionsContainer
                          companyName={PORTFOLIO_STATIC_COMPANY}
                          feedbackLabel={PORTFOLIO_DIVERSIFICATION_BY_COMPANY}
                          exportDarkUrl={downloadDiversificationByCompanyUrl}
                        />
                      </>
                    )}
                  </DeferRenderUntilIntersecting>
                ) : (
                  <NoData />
                )
              }
            />
            <hr />
          </>
        )}

        <ReportSubSection
          title="Diversification Across Industries"
          trackingProps={{ subject: 'chart' }}
          body={
            holdings &&
            holdings.length > 0 &&
            totalValue !== null &&
            fundValue !== null ? (
              <DeferRenderUntilIntersecting preRenderHeight={400}>
                {() => (
                  <>
                    <ScrollPane ref={scrollPane}>
                      <DendogramChart
                        portfolioName={portfolioName}
                        totalValue={totalValue}
                        fundValue={fundValue}
                        holdings={holdings}
                        modifier={`${portfolioId}-${lastUpdated}`}
                      />
                    </ScrollPane>
                    <ChartActionsContainer
                      companyName={PORTFOLIO_STATIC_COMPANY}
                      feedbackLabel={PORTFOLIO_DIVERSIFICATION_BY_INDUSTRY}
                      exportDarkUrl={downloadDiversificationByIndustryUrl}
                    />
                  </>
                )}
              </DeferRenderUntilIntersecting>
            ) : (
              <NoData />
            )
          }
        />
      </ReportSection>
    );
  }
}

export default Diversification;
